import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight, Lock, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { registerUser } from "@/entities/auth/api";
import { ApiError } from "@/shared/api/http";
import { Button } from "@/shared/ui/button";
import { AuthShell } from "@/widgets/AuthShell";

const schema = z
  .object({
    email: z.string().email("Введите корректный email"),
    password: z.string().min(10, "Пароль не короче 10 символов"),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    path: ["confirm"],
    message: "Пароли не совпадают",
  });
type FormValues = z.infer<typeof schema>;

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", confirm: "" },
  });

  const mutation = useMutation({
    mutationFn: ({ email, password }: FormValues) =>
      registerUser({ email, password }),
    onSuccess: (data) => {
      if (data.is_approved) {
        navigate("/login?registered=true&approved=true", { replace: true });
      } else {
        navigate("/register/pending", { replace: true });
      }
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.code === "EMAIL_TAKEN") {
          form.setError("email", { message: "Этот email уже зарегистрирован" });
          return;
        }
        if (err.code === "WEAK_PASSWORD") {
          form.setError("password", { message: err.message });
          return;
        }
      }
      form.setError("email", { message: "Не удалось зарегистрироваться" });
    },
  });

  return (
    <AuthShell glow="cyan">
      <div className="glass-card p-8">
        <h2 className="text-2xl font-bold tracking-tight text-white">
          Регистрация <span className="text-gradient">эксперта</span>
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Если вы прошли курс — регистрируйтесь на тот же email, доступ откроется автоматически.
        </p>

        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="mt-6 flex flex-col gap-4"
        >
          <Field
            label="Email"
            icon={<Mail className="h-4 w-4" />}
            placeholder="you@mail.com"
            type="email"
            autoComplete="email"
            hint="Этот email мы сверим с курсом"
            error={form.formState.errors.email?.message}
            register={form.register("email")}
          />
          <Field
            label="Пароль"
            icon={<Lock className="h-4 w-4" />}
            placeholder="не короче 10 символов"
            type="password"
            autoComplete="new-password"
            error={form.formState.errors.password?.message}
            register={form.register("password")}
          />
          <Field
            label="Подтверждение"
            icon={<Lock className="h-4 w-4" />}
            placeholder="ещё раз"
            type="password"
            autoComplete="new-password"
            error={form.formState.errors.confirm?.message}
            register={form.register("confirm")}
          />
          <Button
            type="submit"
            className="mt-1 w-full justify-center bg-gradient-primary text-white shadow-glow-purple hover:shadow-glow-purple-lg"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "Создаём…" : "Создать аккаунт"} <ArrowRight className="h-4 w-4" />
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          Уже есть аккаунт? <Link to="/login" className="font-semibold text-purple hover:underline">Войти</Link>
        </div>
      </div>
    </AuthShell>
  );
}

function Field(props: {
  label: string;
  icon?: React.ReactNode;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  hint?: string;
  error?: string;
  register: ReturnType<typeof useForm<FormValues>>["register"] extends (...args: never[]) => infer R ? R : never;
}): JSX.Element {
  const { label, icon, hint, error, register, ...rest } = props;
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-foreground/90">{label}</span>
      <div
        className={
          "flex items-center gap-2 rounded-lg border bg-white/[0.04] px-3 transition-colors focus-within:border-purple focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.15)]" +
          (error ? " border-pink/50" : " border-white/10")
        }
      >
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <input
          {...register}
          {...rest}
          className="h-10 w-full bg-transparent text-sm text-white outline-none placeholder:text-muted-foreground"
        />
      </div>
      {error ? (
        <span className="text-xs text-pink">{error}</span>
      ) : hint ? (
        <span className="text-xs text-muted-foreground">{hint}</span>
      ) : null}
    </label>
  );
}
